/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, FormText, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { convertDateTimeFromServer, convertDateTimeToServer, displayDefaultDateTime } from 'app/shared/util/date-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getStudies } from 'app/entities/study/study.reducer';
import { getEntities as getEquipment } from 'app/entities/equipment/equipment.reducer';
import { createEntity, getEntity, reset, updateEntity } from './series.reducer';

export const SeriesUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const studies = useAppSelector(state => state.study.entities);
  const equipment = useAppSelector(state => state.equipment.entities);
  const seriesEntity = useAppSelector(state => state.series.entity);
  const loading = useAppSelector(state => state.series.loading);
  const updating = useAppSelector(state => state.series.updating);
  const updateSuccess = useAppSelector(state => state.series.updateSuccess);

  const handleClose = () => {
    navigate(`/series${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getStudies({}));
    dispatch(getEquipment({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.seriesNumber !== undefined && typeof values.seriesNumber !== 'number') {
      values.seriesNumber = Number(values.seriesNumber);
    }
    values.seriesDateTime = convertDateTimeToServer(values.seriesDateTime);
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.timestampentry !== undefined && typeof values.timestampentry !== 'number') {
      values.timestampentry = Number(values.timestampentry);
    }
    if (values.lastupdated !== undefined && typeof values.lastupdated !== 'number') {
      values.lastupdated = Number(values.lastupdated);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }

    const entity = {
      ...seriesEntity,
      ...values,
      study: studies.find(it => it.id.toString() === values.study?.toString()),
      equipment: equipment.find(it => it.id.toString() === values.equipment?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {
          seriesDateTime: displayDefaultDateTime(),
        }
      : {
          ...seriesEntity,
          seriesDateTime: convertDateTimeFromServer(seriesEntity.seriesDateTime),
          study: seriesEntity?.study?.id,
          equipment: seriesEntity?.equipment?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.series.home.createOrEditLabel" data-cy="SeriesCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.series.home.createOrEditLabel">Create or edit a Series</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="series-id"
                  label={translate('tinyMedicRestApp.series.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.series.seriesInstanceUid')}
                id="series-seriesInstanceUid"
                name="seriesInstanceUid"
                data-cy="seriesInstanceUid"
                type="text"
                validate={{
                  maxLength: { value: 100, message: translate('entity.validation.maxlength', { max: 100 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.series.seriesNumber')}
                id="series-seriesNumber"
                name="seriesNumber"
                data-cy="seriesNumber"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.series.seriesDescription')}
                id="series-seriesDescription"
                name="seriesDescription"
                data-cy="seriesDescription"
                type="text"
                validate={{
                  maxLength: { value: 100, message: translate('entity.validation.maxlength', { max: 100 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.series.bodyPartExamined')}
                id="series-bodyPartExamined"
                name="bodyPartExamined"
                data-cy="bodyPartExamined"
                type="text"
                validate={{
                  maxLength: { value: 100, message: translate('entity.validation.maxlength', { max: 100 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.series.patientPosition')}
                id="series-patientPosition"
                name="patientPosition"
                data-cy="patientPosition"
                type="text"
                validate={{
                  maxLength: { value: 30, message: translate('entity.validation.maxlength', { max: 30 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.series.laterality')}
                id="series-laterality"
                name="laterality"
                data-cy="laterality"
                type="text"
                validate={{
                  maxLength: { value: 100, message: translate('entity.validation.maxlength', { max: 100 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.series.protocolName')}
                id="series-protocolName"
                name="protocolName"
                data-cy="protocolName"
                type="text"
                validate={{
                  maxLength: { value: 100, message: translate('entity.validation.maxlength', { max: 100 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.series.operatorsName')}
                id="series-operatorsName"
                name="operatorsName"
                data-cy="operatorsName"
                type="text"
                validate={{
                  maxLength: { value: 50, message: translate('entity.validation.maxlength', { max: 50 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.series.seriesDateTime')}
                id="series-seriesDateTime"
                name="seriesDateTime"
                data-cy="seriesDateTime"
                type="datetime-local"
                placeholder="YYYY-MM-DD HH:mm"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.series.microSec')}
                id="series-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.series.timestampentry')}
                id="series-timestampentry"
                name="timestampentry"
                data-cy="timestampentry"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.series.lastupdated')}
                id="series-lastupdated"
                name="lastupdated"
                data-cy="lastupdated"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.series.cursor')}
                id="series-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.series.active')}
                id="series-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.series.statusCode')}
                id="series-statusCode"
                name="statusCode"
                data-cy="statusCode"
                type="text"
                validate={{
                  maxLength: { value: 30, message: translate('entity.validation.maxlength', { max: 30 }) },
                }}
              />
              <ValidatedField
                id="series-study"
                name="study"
                data-cy="study"
                label={translate('tinyMedicRestApp.series.study')}
                type="select"
                required
              >
                <option value="" key="0" />
                {studies
                  ? studies.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="series-equipment"
                name="equipment"
                data-cy="equipment"
                label={translate('tinyMedicRestApp.series.equipment')}
                type="select"
                required
              >
                <option value="" key="0" />
                {equipment
                  ? equipment.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/series" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default SeriesUpdate;
